import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../utils/supabase';

declare global {
  interface Window {
    REACT_APP_NAVIGATE: (path: string) => void;
  }
}

export default function Register() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    shelterName: '',
    password: '',
    city: '',
    state: '',
    phone: ''
  });
  const [loading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState('');


  // Function to validate email format
  const validateEmailFormat = (email: string) => {
    if (!email) {
      setEmailError('');
      return false;
    }
    
    // Basic format check
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setEmailError('Por favor ingresa un email válido');
      return false;
    }
    
    setEmailError('');
    return true;
  };


  // Function to send welcome email
  const sendWelcomeEmail = async (userData: {
    email: string;
    username: string;
    shelterName: string;
  }) => {
    try {
      const response = await fetch('https://huellitas-unidas-server.vercel.app/api/send-confirmation-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: userData.email,
          username: userData.username,
          shelterName: userData.shelterName
        }),
      });

      const result = await response.json();
      if (result.success) {
        console.log('✅ Welcome email sent successfully to:', userData.email);
        return true;
      } else {
        console.error('❌ Failed to send welcome email:', result.error);
        // Don't block registration if email fails
        return true;
      }
    } catch (error) {
      console.error('❌ Error sending welcome email:', error);
      // Don't block the registration process if email fails due to server issues
      return true;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validar formato de email
    if (!validateEmailFormat(formData.email)) {
      alert('Por favor ingresa un email válido');
      return;
    }

    // Validar que todos los campos estén completos
    if (!formData.email || !formData.shelterName || !formData.password || !formData.city || !formData.state || !formData.phone) {
      alert('Por favor completa todos los campos');
      return;
    }

    setLoading(true);

    try {
      // Create user in Supabase Auth
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
      });

      if (authError) {
        console.error('Auth error:', authError);
        alert('Error al crear la cuenta: ' + authError.message);
        return;
      }

      if (!authData.user) {
        alert('Error al crear la cuenta');
        return;
      }

      // Insert user data into users table
      const { data, error } = await supabase
        .from('users')
        .insert([
          {
            id: authData.user.id,
            email: formData.email,
            username: formData.email.split('@')[0], // Use email prefix as username
            shelter_name: formData.shelterName,
            city: formData.city,
            state: formData.state,
            phone: formData.phone,
            plan: null,
            plan_status: 'inactive',
            subscription_status: 'inactive',
            trial_end_date: null,
            email_confirm: false
          }
        ])
        .select()
        .single();

      if (error) {
        console.error('Database error:', error);
        alert('Error al guardar los datos del usuario');
        return;
      }

      // Guardar datos del usuario en localStorage para el panel admin
      localStorage.setItem('user_data', JSON.stringify(data));
      
      // Send welcome email to the new user (non-blocking)
      sendWelcomeEmail({
        email: formData.email,
        username: formData.email.split('@')[0], // Use email prefix as username
        shelterName: formData.shelterName
      }).catch(error => {
        console.error('Welcome email failed:', error);
        // Don't block registration if email fails
      });

      alert('¡Cuenta creada exitosamente! Revisa tu email para confirmar tu cuenta.');
      navigate('/login');
    } catch (error) {
      console.error('Error during registration:', error);
      alert('Error durante el registro. Por favor intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <img
                src="/images/image.png"
                alt="Huellitas Unidas Logo"
                className="w-10 h-10 object-contain mr-3"
              />
              <span className="text-xl font-bold text-gray-900">Huellitas Unidas</span>
            </div>
            <button 
              onClick={() => navigate('/')}
              className="text-gray-600 hover:text-gray-900 text-sm font-medium"
            >
              Volver al inicio
            </button>
          </div>
        </div>
      </div>

      {/* Page Title Section */}
      <div className="bg-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Crear Cuenta
            </h1>
            <p className="text-lg text-gray-600">
              Únete a nuestra comunidad de refugios comprometidos con el bienestar animal
            </p>
          </div>
        </div>
      </div>

      {/* Registration Form */}
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email *
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => {
                  setFormData({ ...formData, email: e.target.value });
                  validateEmailFormat(e.target.value);
                }}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                  emailError ? 'border-red-500' : emailError === '' && formData.email ? 'border-green-500' : 'border-gray-300'
                }`}
                placeholder="siresec916@memeazon.com"
                required
              />
              {emailError && <p className="text-red-500 text-sm mt-1">{emailError}</p>}
              {!emailError && formData.email && (
                <p className="text-green-500 text-sm mt-1">✓ Email válido</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nombre del Refugio *
              </label>
              <input
                type="text"
                value={formData.shelterName}
                onChange={(e) => setFormData({ ...formData, shelterName: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                placeholder="siresec916"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Contraseña (mínimo 6 caracteres) *
              </label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                placeholder="••••••••"
                required
              />
              <p className="text-xs text-gray-500 mt-1">Mínimo 6 caracteres</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ciudad *
                </label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  placeholder="Mexico"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Estado *
                </label>
                <input
                  type="text"
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  placeholder="CDMX"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Teléfono *
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                placeholder="+52 55 1234 5678"
                required
              />
            </div>

            <div className="flex items-start">
              <input
                type="checkbox"
                id="terms"
                className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded mt-1"
                required
              />
              <label htmlFor="terms" className="ml-2 text-sm text-gray-600">
                Acepto los términos y condiciones y la política de privacidad
              </label>
            </div>

            <button
              type="submit"
              disabled={loading || emailError !== ''}
              className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-gray-400 text-white font-semibold py-4 px-6 rounded-lg transition-colors duration-300 flex items-center justify-center"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Verificando usuario...
                </>
              ) : (
                <>
                  <i className="ri-user-add-fill mr-2"></i>
                  Crear Cuenta y Continuar
                </>
              )}
            </button>

            <p className="text-center text-xs text-gray-500">
              Al registrarte, podrás acceder inmediatamente a tu panel de administración
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}